import React, { forwardRef, SetStateAction, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWindowDimensions } from '../../../hooks/useDimensions';
import ActionModal from '../../Modal/ActionModal';
import { ActionModalComponentProps } from '../../Modal/ActionModal/ActionModal';
import {
  ClearButtonContainer,
  ClearButtonIcon,
  ContentRightWrapper,
  SearchButton,
  SearchContainer,
  StyledSearchIcon,
  StyledSearchInput,
} from './SearchInput.styles';

interface SearchInputProps {
  autofocus?: boolean;
  placeholder?: string;
  onTextChange?: (text: string) => void;
  clearable?: boolean;
  onClick?: () => void;
  searchTextLocal?: string;
  setSearchTextLocal?: React.Dispatch<SetStateAction<string>>;
  className?: string;
  desktopWidth?: string;
  maxDesktopWidth?: string;
  mobileTabletWidth?: string;
  useMobileBottomsheet?: boolean; // If true, then the search input will open a bottomsheet on mobile.
  autoHeight?: boolean;
  mobileHeight?: string;
  contentRight?: React.ReactNode;
  contentRightWidth?: string;
}

const SearchInput = ({
  autofocus,
  placeholder,
  onTextChange,
  clearable = true,
  onClick,
  searchTextLocal,
  setSearchTextLocal,
  className,
  desktopWidth,
  maxDesktopWidth,
  mobileTabletWidth,
  useMobileBottomsheet,
  autoHeight = false,
  mobileHeight,
  contentRight,
  contentRightWidth,
}: SearchInputProps) => {
  // Dependencies
  const { isInTablet } = useWindowDimensions();
  const { t } = useTranslation();

  // State
  const [value, setValue] = useState('');

  // Refs
  const inputRef = useRef<HTMLInputElement>();
  const searchModalRef = useRef(null);

  // Derived State
  const displayClearableButton = clearable && value !== '';
  const displayedPlaceholder = placeholder ?? t('common.search');

  // Handlers
  const handleClick = () => {
    if (isInTablet) {
      searchModalRef?.current?.open();
    }

    onClick?.();
  };

  const handleFocus = () => {
    inputRef?.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setValue(text);
    onTextChange?.(text);
  };

  const handleClear = () => {
    setValue('');
    onTextChange?.('');
    inputRef?.current?.focus();
  };

  useEffect(() => {
    setValue(searchTextLocal);
  }, [searchTextLocal]);

  // NOTE: This is a known anti-pattern but the refactor exceeds my available scope.
  // SubComponents
  const SearchModalInput = forwardRef(({ onFinish }: ActionModalComponentProps, ref): any => {
    const [text, setText] = useState(searchTextLocal);

    useImperativeHandle(ref, () => ({
      submit: () => {
        setSearchTextLocal(text);
        onTextChange?.(text);
        onFinish?.();
      },
    }));

    return (
      <SearchContainer $focused={true} className={className}>
        <StyledSearchInput
          placeholder={displayedPlaceholder}
          $focused={true}
          $alwaysVisible={true}
          labelLeft={<StyledSearchIcon />}
          onChange={(e: any) => setText(e.target.value as string)}
          value={text}
          contentRight={<ContentRightWrapper $addRightPadding={clearable}>{contentRight}</ContentRightWrapper>}
          $contentRightWidth={contentRightWidth}
        />
      </SearchContainer>
    );
  });

  return (
    <>
      <SearchContainer
        onFocus={handleFocus}
        $focused
        $desktopWidth={desktopWidth}
        $maxDesktopWidth={maxDesktopWidth}
        $mobileTabletWidth={mobileTabletWidth}
        onClick={handleClick}
        className={className}
        $autoHeight={autoHeight}
        $mobileHeight={mobileHeight}
      >
        {useMobileBottomsheet ? (
          <SearchButton>
            <StyledSearchIcon />
          </SearchButton>
        ) : (
          <StyledSearchInput
            aria-label="search-input"
            labelLeft={<StyledSearchIcon />}
            ref={inputRef}
            placeholder={displayedPlaceholder}
            onChange={handleChange}
            value={value}
            $focused
            autoFocus={autofocus}
            contentRight={<ContentRightWrapper $addRightPadding={clearable}>{contentRight}</ContentRightWrapper>}
            $contentRightWidth={contentRightWidth}
          />
        )}
        {displayClearableButton ? (
          <ClearButtonContainer onClick={handleClear}>
            <ClearButtonIcon />
          </ClearButtonContainer>
        ) : null}
      </SearchContainer>
      {useMobileBottomsheet && (
        <ActionModal
          ref={searchModalRef}
          title={displayedPlaceholder}
          submitButtonTitle={t('common.search')}
          Component={SearchModalInput}
        />
      )}
    </>
  );
};

export default SearchInput;
